import Component from '@glimmer/component'
import { action } from '@ember/object'
import { service } from '@ember/service'
import type { IntlService } from 'ember-intl'
import PopoverStandardDropdown from '@blakeelearning/popovers/components/popover/dropdowns/standard'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { t } from 'ember-intl'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import Pill, { PILL_APPEARANCE } from 'teacher-ui-client/components/base/pill'
import { CheckboxGroup, type OptionSet } from 'teacher-ui-client/components/base/checkbox-group'
import type { ThemeKeys } from 'teacher-ui-client/utils/themes'
import type { TOC } from '@ember/component/template-only'
import {
  CategoriesByGroup,
  CategoryGroup,
  coalesceCategoryIds,
} from 'teacher-ui-client/components/book-library/additional-filters-support'
import type {
  CategoryGroupType,
  FormChangeset,
} from 'teacher-ui-client/components/book-library/additional-filters-support'

interface Signature {
  Element: HTMLDivElement
  Args: {
    theme?: ThemeKeys
    changeset: FormChangeset
    onCommit: (categories: string[], ancillaryCategories: string[]) => void
  }
}

/**
 * Displays the additional filters row which consists of 3 columns titled
 * 'FICTION', 'NON-FICTION' and 'SPECIAL ADDITIONAL FILTERS' and each
 * consisting of a checkbox group.
 *
 * An onClose function component parameter will be called when the
 * component's drop-down is closed, the function accepts a single object
 * argument which contains the keys/selected values for each checkbox group.
 */
export default class AdditionalFilters extends Component<Signature> {
  @service intl!: IntlService

  get categoryFilterFictionLabel() {
    return this.intl.t(`classifications.fiction`)
  }

  get categoryFilterNonFictionLabel() {
    return this.intl.t(`classifications.nonfiction`)
  }

  get categoryFilterAncillaryLabel() {
    return this.intl.t(`bookLibrary.components.additionalFilters.specialHeading`)
  }

  @action
  onUpdate({ id: categoryFilterType }: { id: string }, selectedCategories: string[]) {
    this.args.changeset.set(categoryFilterType, selectedCategories)
  }

  @action
  clearAll() {
    this.args.changeset.set(CategoryGroup.fiction, [])
    this.args.changeset.set(CategoryGroup.nonfiction, [])
    this.args.changeset.set(CategoryGroup.ancillary, [])
  }

  @action
  onClose() {
    this.onCommit()
  }

  @action
  onCommit() {
    const categories = coalesceCategoryIds([...this.args.changeset.fiction, ...this.args.changeset.nonfiction])
    this.args.onCommit(categories, this.args.changeset.ancillary)
  }

  categoryIdToName(categoryId: string) {
    switch (categoryId) {
      case 'fiction.*':
        return this.intl.t(`bookLibrary.allFiction`)
      case 'nonfiction.*':
        return this.intl.t(`bookLibrary.allNonfiction`)
      default: {
        const categoryIdKey = categoryId.split('.')[1]
        return this.intl.t(`bookLibrary.categories.${categoryIdKey}`)
      }
    }
  }

  get fictionOptionSet(): OptionSet<string> {
    return {
      id: CategoriesByGroup.fiction.id,
      legend: this.categoryFilterFictionLabel,
      options: CategoriesByGroup.fiction.categories.map((categoryId) => ({
        id: categoryId,
        label: this.categoryIdToName(categoryId),
      })),
    }
  }

  get nonFictionOptionSet(): OptionSet<string> {
    return {
      id: CategoriesByGroup.nonfiction.id,
      legend: this.categoryFilterNonFictionLabel,
      options: CategoriesByGroup.nonfiction.categories.map((categoryId) => ({
        id: categoryId,
        label: this.categoryIdToName(categoryId),
      })),
    }
  }

  get ancillaryOptionSet(): OptionSet<string> {
    return {
      id: 'ancillary',
      legend: this.categoryFilterAncillaryLabel,
      options: [
        {
          id: '*.excluded',
          label: this.categoryIdToName('*.excluded'),
        },
        {
          id: '*.fluency_texts',
          label: this.categoryIdToName('*.fluency_texts'),
        },
        {
          id: '*.hi_lo',
          label: this.categoryIdToName('*.hi_lo'),
        },
        {
          id: '*.phonics',
          label: this.categoryIdToName('*.phonics'),
        },
        {
          id: '*.spanish',
          label: this.categoryIdToName('*.spanish'),
        },
      ],
    }
  }

  get pillsViewModel() {
    const categoryViewModel = (id: string) => ({ id, label: this.categoryIdToName(id) })
    const fictionViewModel = coalesceCategoryIds(this.args.changeset.fiction || []).map(categoryViewModel)
    const nonFictionViewModel = coalesceCategoryIds(this.args.changeset.nonfiction || []).map(categoryViewModel)
    const ancillaryCategoriesViewModel = (this.args.changeset.ancillary || []).map(categoryViewModel)
    return {
      fiction: fictionViewModel,
      nonfiction: nonFictionViewModel,
      ancillary: ancillaryCategoriesViewModel,
    }
  }

  @action
  onRemovePill(categoryGroup: CategoryGroupType, filterId: string) {
    if (filterId.endsWith('.*')) {
      this.args.changeset.set(categoryGroup, [])
    } else {
      const categoryIds = this.args.changeset[categoryGroup]
      const withoutFilter = categoryIds.filter((category) => category !== filterId)
      this.args.changeset.set(categoryGroup, withoutFilter)
    }
    this.onCommit()
  }

  <template>
    <div ...attributes>
      <PopoverStandardDropdown @onClose={{this.onClose}} as |dropdown status|>
        <button
          data-test-dropdown-trigger
          type="button"
          class="text-dusty-black-250 rounded-t-md border-t border-r border-l p-4 font-semibold whitespace-nowrap uppercase select-none
            {{if status.isOpen 'border-dusty-black-100 shadow-xl' 'border-transparent'}}"
          {{dropdown.makeTrigger}}
        >
          <span class="mr-6">{{t "bookLibrary.pages.search.moreFilters"}}</span>
          <PhosphorIcon @icon={{if status.isOpen "phosphor-caret-up" "phosphor-caret-down"}} class="h-3.5 w-3.5" />
        </button>
        <dropdown.content>
          <div
            data-test-dropdown-content
            class="border-dusty-black-100 rounded-b-md border-r border-b border-l bg-white leading-10 whitespace-nowrap shadow-xl"
          >
            {{! Content top border, should line up with the controls border }}
            <div class="border-dusty-black-50 mx-4 border-t-2"></div>
            <div class="flex items-start px-12 pt-4 pb-2">
              <CheckboxGroup
                class="inline-block w-64"
                @optionSet={{this.fictionOptionSet}}
                @selectAllLabel={{t "bookLibrary.allBooksOfType" type=this.categoryFilterFictionLabel}}
                @checkedOptionIds={{@changeset.fiction}}
                @onSelectionChange={{this.onUpdate}}
              />
              <CheckboxGroup
                class="inline-block w-64"
                @optionSet={{this.nonFictionOptionSet}}
                @selectAllLabel={{t "bookLibrary.allBooksOfType" type=this.categoryFilterNonFictionLabel}}
                @checkedOptionIds={{@changeset.nonfiction}}
                @onSelectionChange={{this.onUpdate}}
              />
              <CheckboxGroup
                class="inline-block w-64"
                @optionSet={{this.ancillaryOptionSet}}
                @checkedOptionIds={{@changeset.ancillary}}
                @onSelectionChange={{this.onUpdate}}
              />
              <button
                type="reset"
                class="dusty-black-250 absolute right-12 bottom-6 underline"
                {{on "click" this.clearAll}}
              >
                <PhosphorIcon @icon="phosphor-arrow-counter-clockwise-bold" class="mr-1" />{{t
                  "bookLibrary.components.clearAllFilters.label"
                }}
              </button>
            </div>
          </div>
        </dropdown.content>
      </PopoverStandardDropdown>
    </div>
    <div data-test-category-pills>
      <CategoryGroupPills
        @categories={{this.pillsViewModel.fiction}}
        @categoryGroup={{CategoryGroup.fiction}}
        @theme={{@theme}}
        @onRemovePill={{this.onRemovePill}}
      />
      <CategoryGroupPills
        @categories={{this.pillsViewModel.nonfiction}}
        @categoryGroup={{CategoryGroup.nonfiction}}
        @theme={{@theme}}
        @onRemovePill={{this.onRemovePill}}
      />
      <CategoryGroupPills
        @categories={{this.pillsViewModel.ancillary}}
        @categoryGroup={{CategoryGroup.ancillary}}
        @theme={{@theme}}
        @onRemovePill={{this.onRemovePill}}
      />
    </div>
  </template>
}

interface CategoryGroupPillsSignature {
  Args: {
    categories: { id: string; label: string }[]
    categoryGroup: CategoryGroupType
    onRemovePill: (categoryGroup: CategoryGroupType, categoryId: string) => void
    theme?: ThemeKeys
  }
}

const CategoryGroupPills: TOC<CategoryGroupPillsSignature> = <template>
  {{#each @categories as |category|}}
    <Pill
      class="group mt-4 px-2 font-semibold uppercase"
      @theme={{@theme}}
      @appearance={{PILL_APPEARANCE.minimal}}
      {{on "click" (fn @onRemovePill @categoryGroup category.id)}}
    >
      {{category.label}}
      <PhosphorIcon class="invisible group-hover:visible group-focus:visible" @icon="phosphor-x-bold" />
    </Pill>
  {{/each}}
</template>
